/**
 * Find Transcript Search Results
 */
// leaving off stemming for now until we hear customer feedback
// var stem = require("wink-porter2-stemmer");
import cleanWord from "./cleanWord";
import { Paragraph } from "../types/transcriptApiTypes";

export interface Match {
  paragraphIndex: number;
  paragraphMillisecond: number;
  words: {
    index: number;
    millisecond: number;
    italic: boolean;
    text: string;
  }[];
}

/**
 * NOTE: a Word (uppercase) is really a paragraph cell that may have multiple
 * words in it. So we need to test if our Word has multiple words in it when
 * searching. Good to keep that in mind as you are reading this.
 *
 * This is performance sensitive much like paragraphsToTranscriptHTML, so
 * stick with plain old loops over map/reduce for paragraph/words and prefer
 * mutation within loops. Pull in a front-end expert if you need help.
 *
 * 3 nested loops looks scary, but the inner loop is small (search term length).
 * Complexity: O(totalWords * searchTerms)
 */
const findSearchMatches = (paragraphs: Paragraph[], string: string) => {
  const matches: Match[] = [];
  const searchTerms = string
    .trim()
    .split(" ")
    .map((item) => cleanWord(item))
    .filter((item) => item !== "");

  for (let index = 0; index < paragraphs.length; index += 1) {
    const paragraph = paragraphs[index];

    let potentialMatch: Match | null = null;
    let currentMatchIndex = 0;

    for (
      let wordIndex = 0;
      wordIndex < paragraph.words?.length ?? 0;
      wordIndex += 1
    ) {
      const word = { ...paragraph.words[wordIndex], index: wordIndex };
      const cleanedWord = cleanWord(word.text);
      const remainingTerms = searchTerms.length - currentMatchIndex;

      // The inner loop here attempts to find as many search terms as it can
      // match within each Word cell. Again, a Word (paragraph cell) may have
      // multiple words that we match against.
      for (let matchCount = 0; matchCount < remainingTerms; matchCount += 1) {
        const termsTaken = remainingTerms - matchCount;

        const remainingSearch = searchTerms
          .slice(currentMatchIndex, currentMatchIndex + termsTaken)
          .join(" ");

        if (cleanedWord.includes(remainingSearch)) {
          if (potentialMatch) {
            potentialMatch.words.push(word);
          } else {
            potentialMatch = {
              paragraphIndex: index,
              paragraphMillisecond: paragraph.millisecond,
              words: [word],
            };
          }

          currentMatchIndex += termsTaken;

          // Once we found all terms, we register a match and reset tracking
          // variables to look for other matches.
          if (currentMatchIndex >= searchTerms.length) {
            matches.push(potentialMatch);
            currentMatchIndex = 0;
            potentialMatch = null;
          }

          break; // got a match, we can move on to the next word
        }
      }
    }
  }

  return matches;
};

export default findSearchMatches;
